import React, { useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaBrain, FaTools } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';

import { Signal, useComputed } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import { SystemMessageType } from '@/features/ai-agents/components/context-menu';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { MarkdownRenderer } from '@/features/ai-agents/chat/markdown-renderer';
import { conditional } from '@/utils/conditional';
import { breakpoints } from '@/components/responsive';
import { isPinnableMessage } from '@/features/ai-agents/chat/default-view-hint';
import { PinButton } from '@/features/ai-agents/components/pin-button';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export interface AgentMessageProps {
  message?: AiChatMessage;
  streamedMessage?: Signal<AiChatMessage | null>;
  isLatestMessage?: boolean;
  isStreamingMessage?: boolean;
  type?: SystemMessageType;
  showReasoning?: boolean;
  showTools?: boolean;
  processedEvents?: Signal<string[]>;
  isProcessing?: Signal<boolean>;
  onRetry?: () => void;
  className?: string;
}

export interface MessageSenderHeaderProps {
  message?: AiChatMessage;
  agentId?: string;
  sessionId?: string;
  isLocalHost?: boolean;
}

export const MessageSenderHeader = ({
  message,
  agentId,
  sessionId,
  isLocalHost,
}: MessageSenderHeaderProps) => {
  const formattedDate = useMemo(() => {
    if (!message) {
      return `Now`;
    }
    const date = new Date(message.created_at * 1000);
    const time = date.toLocaleTimeString([], {
      hour: `2-digit`,
      minute: `2-digit`,
      hour12: false,
    });

    const month = date.toLocaleString(`en-US`, { month: `short` });
    const day = date.getDate().toString().padStart(2, `0`);
    const year = date.getFullYear();

    return `${time} on ${month} ${day}, ${year}`;
  }, [message]);

  const isPinsEnabled = useFeatureBit(FeatureBit.ENABLE_PINS, false);
  const canShowPinButton =
    isPinsEnabled &&
    !!agentId &&
    !!sessionId &&
    !!message?.id &&
    typeof message?.content === `string` &&
    isPinnableMessage(message.content);

  return (
    <Header>
      <HeaderSender>
        <VisuallyLogo key="constant-logo" small />
        <HeaderName key="constant-name">Visually</HeaderName>
        <HeaderDate key="constant-header">{formattedDate}</HeaderDate>
      </HeaderSender>
      {canShowPinButton && (
        <PinButton
          agentId={agentId}
          sessionId={sessionId}
          messageId={message.id}
          isLocalHost={isLocalHost}
        />
      )}
    </Header>
  );
};

export function AgentMessage({
  message,
  streamedMessage,
  isLatestMessage,
  isStreamingMessage,
  showReasoning,
  showTools,
  processedEvents,
  isProcessing,
  onRetry,
  className,
}: AgentMessageProps) {
  useSignals();
  const contentRef = useRef(null);

  const [isReasoningCollapsed, setIsReasoningCollapsed] = React.useState(true);
  const [isToolsCollapsed, setIsToolsCollapsed] = React.useState(true);

  const { agent, currentSessionId, isLocalHost } = useChatSession();

  const currentMessage = useComputed(
    () => streamedMessage?.value ?? message,
  ).value;

  const eventMessage = useComputed(() => {
    if (processedEvents) {
      const value = processedEvents?.value;
      if (value.length > 0) {
        return value[value.length - 1];
      }
    }
    return ``;
  });

  return (
    <Wrapper className={`agent-message-wrapper ${className}`}>
      <MessageSenderHeader
        message={currentMessage}
        agentId={agent?.id}
        sessionId={currentSessionId?.value}
        isLocalHost={isLocalHost?.value}
      />
      <AgentMessageContentWrapper
        className={isProcessing?.value ? `processing` : ``}
        ref={contentRef}
      >
        {showReasoning &&
          currentMessage?.extra_data?.reasoning_steps?.length > 0 && (
            <CollapseSection
              collasped={isReasoningCollapsed}
              onClick={() => setIsReasoningCollapsed((prev) => !prev)}
            >
              <div className="collapse-title">
                <FaBrain />
                Reasoning
              </div>
              <div
                className="collapse-list"
                style={{ display: isReasoningCollapsed ? `none` : `flex` }}
              >
                {currentMessage?.extra_data?.reasoning_steps?.map((step) => (
                  <div
                    className="collapse-row"
                    key={step.title + step.reasoning}
                  >
                    <div className="collapse-header">{step?.title}</div>
                    <div className="collapse-content">{step?.reasoning}</div>
                  </div>
                ))}
              </div>
            </CollapseSection>
          )}

        {showTools && currentMessage?.tool_calls?.length > 0 && (
          <CollapseSection
            collasped={isToolsCollapsed}
            onClick={() => setIsToolsCollapsed((prev) => !prev)}
          >
            <div className="collapse-title">
              <FaTools />
              Tool Calls
            </div>
            <div
              className="collapse-list"
              style={{ display: isToolsCollapsed ? `none` : `flex` }}
            >
              {currentMessage?.tool_calls?.map((tool) => (
                <div className="collapse-row" key={tool.tool_call_id}>
                  <div className="collapse-header">{tool?.tool_name}</div>
                  <div className="collapse-content">
                    {tool?.tool_args?.[`title`]}
                  </div>
                </div>
              ))}
            </div>
          </CollapseSection>
        )}

        {eventMessage?.value && (
          <EventWrapper>
            <FaBrain className="icon" color="#858d94" size={25} />
            <EventContent>{eventMessage}</EventContent>
          </EventWrapper>
        )}

        {conditional(
          <TypingIndicator />,
          !!currentMessage?.streamingError,
          <>
            <ErrorMessage>{currentMessage?.content}</ErrorMessage>
            {onRetry && (
              <RetryButton onClick={onRetry}>
                <MdRefresh size={16} />
                Retry
              </RetryButton>
            )}
          </>,
          !!currentMessage?.content,
          <MarkdownRenderer
            isLatestMessage={isLatestMessage}
            isStreamingMessage={isStreamingMessage}
          >
            {currentMessage?.content}
          </MarkdownRenderer>,
        )}
      </AgentMessageContentWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.5rem;

  font-family: Eesti, sans-serif;
  font-size: 1.4rem;

  .gatsby-image-wrapper {
    width: 2rem;
    height: 2rem;
  }
`;

const HeaderSender = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`;

const HeaderName = styled.span`
  color: black;
  font-weight: bold;
`;

const HeaderDate = styled.span`
  color: #858d94;
`;

const EventWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 2rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;

  .icon {
    display: none;
    animation: breath 3s ease-in-out infinite;

    @keyframes breath {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }
  }
`;

const EventContent = styled.div`
  width: 100%;
  white-space: pre-line;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  line-height: 1.5;
  font-weight: 600;
  position: relative;
  color: #858d94;

  &&::before {
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    height: 100%;
    width: 150%;
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 70%
    );
    animation: shimmer 6s infinite ease-in-out;
    pointer-events: none;
  }

  @keyframes shimmer {
    0% {
      left: -150%;
    }
    100% {
      left: 150%;
    }
  }
`;

export const AgentMessageContentWrapper = styled.div`
  margin-inline-start: 1.3rem;
  font-family: Eesti, sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.5;
  border-radius: 1rem;
  box-shadow: none;
  border: none;
  background-color: transparent;
  max-width: 90%;
  width: 100%;
  padding: 0.6rem 0.3rem;
  align-self: flex-start;
  overflow: hidden;
  padding-bottom: 5rem;

  pre:has(code):empty {
    display: none;
  }

  code {
    font-family: 'JetBrains Mono', sans-serif;
  }

  pre:has(code) {
    overflow: scroll;
    color: black;
    white-space: break-spaces;
  }

  code:not(pre code) {
    color: #e8912d;
  }

  ${breakpoints.down(`md`)} {
    margin-inline-start: 0;
  }
`;

const CollapseSection = styled.div`
  color: #858d94;
  cursor: pointer;
  margin-bottom: 1.5rem;

  .collapse-title {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 1.5rem;
    margin-bottom: 1rem;

    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
  }

  .collapse-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-inline-start: 1rem;
  }

  .collapse-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .collapse-header {
    font-weight: 600;
  }

  .collapse-content {
    font-weight: 400;
    margin-inline-start: 0.5rem;
  }
`;

const bouncingAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.3rem);
  }
`;

const TypingDotsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-start;
  padding-top: 0.5rem;
`;

const TypingDot = styled.span`
  background-color: #858d94;
  border-radius: 50%;
  height: 0.5rem;
  width: 0.5rem;

  &:nth-child(1) {
    animation: ${bouncingAnimation} 1s infinite 0s;
  }
  &:nth-child(2) {
    animation: ${bouncingAnimation} 1s infinite 0.2s;
  }
  &:nth-child(3) {
    animation: ${bouncingAnimation} 1s infinite 0.4s;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const RetryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: transparent;
  color: #111111;
  border: 1px solid #111111;
  border-radius: 2rem;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background-color: #111111;
    color: white;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(180deg);
  }
`;

export const TypingIndicator = () => (
  <TypingDotsContainer>
    <TypingDot />
    <TypingDot />
    <TypingDot />
  </TypingDotsContainer>
);
